import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Phone, Mic, MicOff, Volume2, VolumeX, PhoneOff, MoreVertical } from "lucide-react";
import Swal from "sweetalert2";
import Calling from "./calling";

const Chat = ({ customerName, customerAvatar, isLive = true, onCall, onEndChat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm ready for the appointment.",
      sender: "customer",
      timestamp: new Date(Date.now() - 60000),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [showEndChatMenu, setShowEndChatMenu] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: "handyman",
      timestamp: new Date(),
    };

    setMessages([...messages, newMessage]);
    setInputMessage("");
    setIsTyping(true);

    // Simulate customer reply after 1-3 seconds
    setTimeout(() => {
      const replies = [
        "Thank you for the update!",
        "Sounds good, see you soon.",
        "Perfect, I'll be waiting.",
        "Got it, thanks!",
        "Understood.",
      ];
      const randomReply = replies[Math.floor(Math.random() * replies.length)];

      const customerReply = {
        id: messages.length + 2,
        text: randomReply,
        sender: "customer",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, customerReply]);
      setIsTyping(false);
    }, Math.random() * 2000 + 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleEndChat = async () => {
    setShowEndChatMenu(false);
    
    const result = await Swal.fire({
      title: 'End Chat?',
      text: "Are you sure you want to end this chat? You can start a new chat anytime.",
      icon: 'question',
      width: 500,
      showCancelButton: true,
      confirmButtonColor: '#EFA938',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, end chat',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#EFA938] hover:bg-[#d78c1f] text-white px-4 py-2 rounded-lg font-semibold',
        cancelButton: 'bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold'
      }
    });

    if (result.isConfirmed) {
      onEndChat?.();
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[9999] h-16 w-16 rounded-full bg-[#EFA938] text-white shadow-lg hover:bg-[#d78c1f] transition-all duration-300 flex items-center justify-center ${
          isOpen ? "scale-90" : "scale-100 hover:scale-110"
        }`}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
        {!isOpen && messages.filter((m) => m.sender === "customer" && !m.read).length > 0 && (
          <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 text-xs flex items-center justify-center">
            {messages.filter((m) => m.sender === "customer" && !m.read).length}
          </span>
        )}
      </button>

      {/* Chat UI */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[9998] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col"
          style={{ width: "20rem", height: "28rem", maxWidth: "calc(100vw - 3rem)", maxHeight: "calc(100vh - 8rem)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-[#EFA938] to-orange-400 dark:from-[#d78c1f] dark:to-orange-500 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Profile Pic with Live Indicator */}
              <div className="relative flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#EFA938] to-orange-400 flex items-center justify-center text-white font-semibold">
                  {customerAvatar ? (
                    <img src={customerAvatar} alt={customerName} className="h-full w-full rounded-full object-cover" />
                  ) : (
                    customerName.charAt(0).toUpperCase()
                  )}
                </div>
                {isLive && (
                  <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white dark:border-slate-800"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white truncate">Customer: {customerName}</p>
                  {isLive && (
                    <span className="text-xs text-white/90 bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Call Button */}
              <button
                onClick={() => {
                  setIsCalling(true);
                  onCall?.();
                }}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                title="Make a call"
              >
                <Phone className="h-4 w-4" />
              </button>
              {/* More Options */}
              <div className="relative">
                <button
                  onClick={() => setShowEndChatMenu(!showEndChatMenu)}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                  title="More options"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
                {showEndChatMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowEndChatMenu(false)}
                    ></div>
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50 min-w-[120px]">
                      <button
                        onClick={handleEndChat}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        End Chat
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50 dark:bg-slate-900">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === "handyman" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                    message.sender === "handyman"
                      ? "bg-[#EFA938] text-white rounded-br-sm"
                      : "bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-600 rounded-bl-sm"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === "handyman" ? "text-white/70" : "text-gray-500 dark:text-gray-400"}`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-bl-sm px-3 py-2">
                  <div className="flex gap-1">
                    <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!isCalling && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-b-2xl">
              <div className="flex items-end gap-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EFA938] focus:border-transparent"
                  style={{ maxHeight: "100px" }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim()}
                  className="px-4 py-2 rounded-lg bg-[#EFA938] text-white hover:bg-[#d78c1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* Calling UI Overlay */}
          {isCalling && (
            <Calling
              customerName={customerName}
              customerAvatar={customerAvatar}
              onHangUp={() => {
                setIsCalling(false);
                onCall?.();
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Chat;

